/**
 * @fileoverview Checkout Form Configuration
 *
 * This file defines the configuration structure and layout rules for the
 * checkout form component, enabling flexible form rendering and validation.
 *
 * Configuration Features:
 * - Field grouping with layout specifications (readonly, editable, display)
 * - Field width control (full, half) and row grouping
 * - Collapsible sections for optional fields
 * - Required field validation rules
 * - Integration with cart total calculations
 *
 * Layout Types:
 * - Compact: Minimal spacing for readonly information
 * - Structured: Organized layout for form input sections
 * - Display: Special handling for summary information
 *
 * Helper Functions:
 * - getFieldConfig: Retrieves configuration for specific fields
 * - getFieldGroup: Determines grouping relationships
 *
 * Form Groups:
 * - Basic Information: Readonly user details
 * - Delivery Address: Editable shipping information with conditional fields
 * - Order Summary: Display-only total calculation integration
 */

/**
 * Configuration interface for individual field layout and behavior
 */
export interface FieldLayoutConfig {
  /** Field width - full takes entire row, half shares row space */
  width?: "full" | "half";
  /** Whether this field is required for form submission */
  required?: boolean;
  /** Whether this section can be collapsed/expanded */
  collapsible?: boolean;
  /** Display title for collapsible sections */
  title?: string;
  /** Array of field names contained in this configuration */
  fields?: string[];
  /** Array of field names to be rendered in a single row */
  row?: string[];
}

/**
 * Configuration interface for field groups within the checkout form
 */
export interface GroupConfig {
  /** Group interaction type - determines field editing capabilities */
  type: "readonly" | "editable" | "display";
  /** Visual layout style for the group */
  layout?: "compact" | "structured";
  /** Whether this group's total should be used for cart calculations */
  useForCartTotal?: boolean;
  /** Field configuration - can be simple array or complex layout object */
  fields: string[] | Record<string, FieldLayoutConfig>;
}

/**
 * Main configuration interface for the entire checkout form structure
 */
export interface CheckoutFormConfig {
  /** Collection of form groups, keyed by group display name */
  groups: Record<string, GroupConfig>;
}

/**
 * Main checkout form configuration defining layout and behavior
 *
 * This configuration drives the dynamic rendering of the checkout form,
 * organizing fields into logical groups with appropriate layouts and validation.
 */
export const checkoutFormConfig: CheckoutFormConfig = {
  groups: {
    "Basic Information": {
      type: "readonly",
      layout: "compact",
      fields: {
        NameGroup: {
          row: ["FirstName", "LastName"],
        },
        EmailAddress: { width: "full" },
      },
    },
    "Delivery Address": {
      type: "editable",
      layout: "structured",
      fields: {
        SitesId: { width: "full" },
        SitesName: { width: "full" },
        AddressLine1: { width: "full", required: true },
        AddressLine2: { width: "full", required: true },
        AddressLines: {
          collapsible: true,
          title: "Additional Address Lines",
          fields: ["AddressLine3", "AddressLine4"],
        },
        LocationGroup: {
          row: ["City", "StateProvince"],
        },
        RegionGroup: {
          row: ["PostalZipCode", "CountriesId"],
        },
        USStatesGroup: {
          row: ["USStatesID"],
        },
      },
    },
    "Order Summary": {
      type: "display",
      useForCartTotal: true,
      fields: ["Total"],
    },
  },
};

/**
 * Retrieves the layout configuration for a specific field within a group
 *
 * @param groupLabel - The display name of the form group
 * @param fieldName - The name of the field to find configuration for
 * @returns The field layout configuration or null if not found
 */
export const getFieldConfig = (groupLabel: string, fieldName: string): FieldLayoutConfig | null => {
  const group = checkoutFormConfig.groups[groupLabel];
  if (!group || Array.isArray(group.fields)) return null;

  // Check if field is directly configured
  if (group.fields[fieldName]) {
    return group.fields[fieldName];
  }

  // Check if field is in a row or collapsible section
  for (const [key, config] of Object.entries(group.fields)) {
    if (config.row?.includes(fieldName)) {
      return config;
    }
    if (config.fields?.includes(fieldName)) {
      return config;
    }
  }

  return null;
};

/**
 * Determines which layout group a field belongs to within a form group
 *
 * @param groupLabel - The display name of the form group
 * @param fieldName - The name of the field to check grouping for
 * @returns The group key the field belongs to, or null if not grouped
 */
export const getFieldGroup = (groupLabel: string, fieldName: string): string | null => {
  const group = checkoutFormConfig.groups[groupLabel];
  if (!group || Array.isArray(group.fields)) return null;

  for (const [key, config] of Object.entries(group.fields)) {
    if (config.row?.includes(fieldName) || config.fields?.includes(fieldName)) {
      return key;
    }
  }

  return null;
};
